/**
 * HistoryTab Component
 * 
 * Display campaign history with status and progress tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Phone, TrendingUp, CheckCircle, XCircle, Pause, Clock } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import EmptyState from '../common/EmptyState';
import CampaignDetailsModal from './CampaignDetailsModal';
import CampaignReportModal from './CampaignReportModal';
import { listCampaigns, getCampaign } from '../../api/outboundApi';
import { useSettings } from '../../contexts/SettingsContext';

const HistoryTab = () => {
  const { selectedLocationId } = useSettings();
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listCampaigns({ locationId: selectedLocationId });
      setCampaigns(response);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId]);

  const fetchCampaignStatuses = useCallback(async (campaignIds) => {
    try {
      // Fetch updated data for each active campaign
      const updates = await Promise.all(
        campaignIds.map(id => getCampaign(id).catch(err => {
          console.error(`Error fetching campaign ${id}:`, err);
          return null;
        }))
      );

      // Update campaigns state with new data
      setCampaigns(prev => 
        prev.map(campaign => {
          const campaignId = campaign.itemId || campaign.campaignId;
          const update = updates.find(u => u && (u.itemId || u.campaignId) === campaignId);
          return update || campaign;
        })
      );
    } catch (error) {
      console.error('Error polling campaign statuses:', error);
    }
  }, []);

  // Fetch campaigns when location changes
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Poll active campaigns for status updates
  useEffect(() => {
    const activeCampaigns = campaigns.filter(c => c.status === 'in_progress');
    
    if (activeCampaigns.length === 0) return;

    // Poll every 5 seconds
    const interval = setInterval(() => {
      const campaignIds = activeCampaigns.map(c => c.itemId || c.campaignId);
      fetchCampaignStatuses(campaignIds);
    }, 5000);

    return () => clearInterval(interval);
  }, [campaigns, fetchCampaignStatuses]);


  const handleViewDetails = (campaign) => {
    setSelectedCampaign(campaign);
    if (campaign.status === 'in_progress' || campaign.status === 'paused') {
      setShowDetailsModal(true);
    } else {
      setShowReportModal(true);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      queued: {
        icon: Clock,
        label: 'Queued',
        className: 'bg-gray-600 text-gray-100',
      },
      in_progress: {
        icon: TrendingUp,
        label: 'In Progress',
        className: 'bg-yellow-600 text-yellow-100',
      },
      paused: {
        icon: Pause,
        label: 'Paused',
        className: 'bg-orange-600 text-orange-100',
      },
      completed: {
        icon: CheckCircle,
        label: 'Completed',
        className: 'bg-green-600 text-green-100',
      },
      failed: {
        icon: XCircle,
        label: 'Failed',
        className: 'bg-red-600 text-red-100',
      },
      cancelled: {
        icon: XCircle,
        label: 'Cancelled',
        className: 'bg-red-600 text-red-100',
      },
    };

    const badge = badges[status] || badges.queued;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.className}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getProgressPercentage = (campaign) => {
    return Math.round((campaign.completedCalls / campaign.totalAppointments) * 100);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AD0E6]"></div>
        <p className="ml-3 text-gray-400">Loading campaigns...</p>
      </div>
    );
  }

  // Empty state
  if (campaigns.length === 0) {
    return (
      <EmptyState
        icon={<Phone className="w-8 h-8 text-gray-400" />}
        title="No Campaigns Yet"
        description="You haven't launched any campaigns yet. Create and launch your first campaign to see it here."
      />
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-white">
            Campaign History ({campaigns.length})
          </h3>
          <p className="text-sm text-gray-400">
            Track and manage your outbound calling campaigns
          </p>
        </div>

        {/* Campaign Table */}
        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 text-xs font-medium py-3 px-2">Campaign Name</th>
                  <th className="text-left text-gray-400 text-xs font-medium py-3 px-2">Appointments</th>
                  <th className="text-left text-gray-400 text-xs font-medium py-3 px-2">Started</th>
                  <th className="text-left text-gray-400 text-xs font-medium py-3 px-2">Status</th>
                  <th className="text-left text-gray-400 text-xs font-medium py-3 px-2">Progress</th>
                  <th className="text-left text-gray-400 text-xs font-medium py-3 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => {
                  const campaignId = campaign.itemId || campaign.campaignId;
                  return (
                    <tr 
                      key={campaignId}
                      className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="text-gray-300 text-sm py-4 px-2 font-medium">
                        {campaign.name}
                      </td>
                      <td className="text-gray-300 text-sm py-4 px-2">
                        {campaign.totalAppointments}
                      </td>
                      <td className="text-gray-300 text-sm py-4 px-2">
                        {formatDate(campaign.startedAt)}
                      </td>
                      <td className="text-gray-300 text-sm py-4 px-2">
                        {getStatusBadge(campaign.status)}
                      </td>
                      <td className="text-gray-300 text-sm py-4 px-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-700 rounded-full h-2 w-24">
                            <div 
                              className="bg-[#3AD0E6] h-2 rounded-full transition-all"
                              style={{ width: `${getProgressPercentage(campaign)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {campaign.completedCalls}/{campaign.totalAppointments} ({getProgressPercentage(campaign)}%)
                          </span>
                        </div>
                      </td>
                      <td className="text-gray-300 text-sm py-4 px-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(campaign)}
                          className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                        >
                          {campaign.status === 'in_progress' || campaign.status === 'paused' ? 'Details' : 'Report'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Modals */}
      <CampaignDetailsModal
        campaign={selectedCampaign}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedCampaign(null);
        }}
        onUpdate={fetchCampaigns}
      />

      <CampaignReportModal
        campaign={selectedCampaign}
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setSelectedCampaign(null);
        }}
      />
    </>
  );
};

export default HistoryTab;

